import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["children", "use"];
import { useContext } from 'react';
import styled from 'foundations-theming/utils';
import { StyledText } from '../../../components/Text/Text';
import { DialogHeaderContext } from './DialogHeaderContext';
import { DialogContext } from '../Dialog/DialogContext';
import { MODAL_H1_USES } from '../ModalDialog/ModalDialog';
import { ConfirmDialogContext } from '../ConfirmDialog/ConfirmDialogContext';
import { filterProps } from '../../../utils/filterProps';
import { getDialogHeaderBackground } from '../Dialog/Dialog';
import { useMapTokenPropsToCSS } from '../../../hooks/useMapTokenPropsToCSS';
import { StyledSpan } from '../Badge/Badge';
import { StyledClickable } from '../Clickable/Clickable';
import { jsx as _jsx } from "react/jsx-runtime";
export const StyledDialogHeader = styled.header.withConfig({
  displayName: "DialogHeader__StyledDialogHeader",
  componentId: "sc-5tmghi-0"
})(({
  $use,
  $cssStyles,
  theme: _theme
}) => {
  const theme = _theme;
  const modalUse = useContext(DialogContext).use;
  const isConfirmDialog = useContext(ConfirmDialogContext).isConfirmDialog;
  const isModalH1Use = MODAL_H1_USES.includes(modalUse);
  const isCenteredUse = modalUse === 'conversational' || modalUse === 'info' || modalUse === 'upgrades';
  const isFullscreen = modalUse === 'fullscreen';
  const isSuccess = modalUse === 'success';
  const isInfo = modalUse === 'info';
  const isDanger = modalUse === 'danger';
  const isUpgrades = modalUse === 'upgrades';
  const typography = isModalH1Use ? theme.typography['heading-600'] : theme.typography['heading-300'];

  // Dialog also renders the background color styling, so in the case of these
  // modal types, we want to fallback to whatever Dialog is rendering. In other
  // cases, we need the background styling as DialogHeader could be rendered outside
  // the context of a Dialog (e.g WizardHeader)
  const isTransparent = isSuccess || isInfo || isDanger || isUpgrades;
  const getBackground = () => {
    if (isTransparent) {
      return {
        background: theme.color['fill-transparent-default']
      };
    }
    if (isFullscreen) {
      return {
        backgroundColor: theme.color['fill-surface-default'],
        backgroundImage: `linear-gradient(-303deg, ${theme.color['fill-secondary-default']}, ${theme.color['fill-positive-default']})`
      };
    }
    return getDialogHeaderBackground({
      theme,
      use: modalUse,
      isConfirmDialog
    });
  };
  return Object.assign({
    [`& ${StyledText}:not(${StyledSpan} ${StyledText})`]: {
      color: modalUse !== 'conversational' && !isConfirmDialog || modalUse === 'danger' ? theme.color['text-primary-on-fill-default'] : undefined,
      fontSize: typography.fontSize,
      fontWeight: typography.fontWeight,
      lineHeight: isModalH1Use ? '40px' : typography.lineHeight
    },
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 0,
    flexShrink: 0,
    borderStartStartRadius: !isFullscreen ? theme.borderRadius['100'] : undefined,
    borderStartEndRadius: !isFullscreen ? theme.borderRadius['100'] : undefined,
    textAlign: isCenteredUse ? 'center' : undefined,
    alignItems: 'center',
    paddingBlock: isModalH1Use ? theme.space['900'] : undefined,
    paddingBlockStart: isModalH1Use ? theme.space['900'] : undefined,
    paddingBlockEnd: modalUse === 'conversational' ? theme.space['50'] : undefined,
    justifyContent: 'space-between'
  }, getBackground(), $use === 'toolbar' && {
    padding: '0',
    borderBottom: 'none',
    backgroundColor: 'transparent'
  }, $cssStyles);
});
export const StyledHeaderInner = styled.div.withConfig({
  displayName: "DialogHeader__StyledHeaderInner",
  componentId: "sc-5tmghi-1"
})(({
  theme: _theme
}) => {
  const theme = _theme;
  const modalUse = useContext(DialogContext).use;
  const isCentered = modalUse === 'conversational' || modalUse === 'info' || modalUse === 'upgrades';
  return {
    WebkitFontSmoothing: 'antialiased',
    display: 'flex',
    alignItems: 'center',
    paddingBlockStart: theme.space['100'],
    paddingBlockEnd: theme.space['100'],
    paddingInlineStart: theme.space['1000'],
    paddingInlineEnd: theme.space['1000'],
    justifyContent: isCentered ? 'center' : undefined,
    minHeight: `calc(${theme.space['1400']} + ${theme.space['100']})`,
    width: '100%',
    [`> ${StyledText}`]: {
      inlineSize: '100%',
      minInlineSize: 0
    },
    [`> * + ${StyledClickable}`]: {
      marginRight: theme.space['500']
    },
    [`> ${StyledClickable}:last-child`]: {
      marginRight: 0
    }
  };
});
export function DialogHeader(_ref) {
  let {
      children,
      use
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const {
    domProps,
    tokenProps
  } = filterProps(rest);
  const cssStyles = useMapTokenPropsToCSS(tokenProps);
  return /*#__PURE__*/_jsx(DialogHeaderContext.Provider, {
    value: {
      isDialogHeader: true
    },
    children: /*#__PURE__*/_jsx(StyledDialogHeader, Object.assign({
      $use: use
    }, domProps, {
      $cssStyles: cssStyles,
      children: /*#__PURE__*/_jsx(StyledHeaderInner, {
        children: children
      })
    }))
  });
}